import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, RefreshCw, LogOut, Plus, CheckCircle, Clock, XCircle, Calendar, Trash2, X } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, paid, pending
  const [showManualEmailForm, setShowManualEmailForm] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings'); // bookings, calendar
  const [blockedDates, setBlockedDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchBookings();
    fetchBlockedDates();
  }, []);

  const fetchBlockedDates = async () => {
    setLoadingDates(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedDates(response.data);
    } catch (err) {
      console.error('Error fetching blocked dates:', err);
    } finally {
      setLoadingDates(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        alert('Error fetching bookings: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (sessionId, customerEmail) => {
    if (!customerEmail || customerEmail === 'N/A') {
      alert('No customer email available for this booking');
      return;
    }

    if (!window.confirm(`Resend confirmation email to ${customerEmail}?`)) {
      return;
    }

    setResendingId(sessionId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/admin/resend-email/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
    } catch (err) {
      alert('Error resending email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResendingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleBlockDate = async (dateStr, reason = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/blocked-dates`,
        { date: dateStr, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBlockedDates();
    } catch (err) {
      if (err.response?.status === 409) {
        alert('This date is already blocked');
      } else {
        alert('Error blocking date: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleUnblockDate = async (dateId) => {
    if (!window.confirm('Are you sure you want to unblock this date?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/blocked-dates/${dateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBlockedDates();
    } catch (err) {
      alert('Error unblocking date: ' + (err.response?.data?.detail || err.message));
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.payment_status === filter;
  });

  const getStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
          <CheckCircle className="w-4 h-4 mr-1" /> Paid
        </span>
      );
    } else if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
          <Clock className="w-4 h-4 mr-1" /> Pending
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
          <XCircle className="w-4 h-4 mr-1" /> {status}
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-yellow-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Manage bookings, calendar, and send emails</p>
            </div>
            <div className="flex gap-3">
              {activeTab === 'bookings' && (
                <Button
                  onClick={() => setShowManualEmailForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Send Manual Email
                </Button>
              )}
              <Button
                onClick={() => { fetchBookings(); fetchBlockedDates(); }}
                variant="outline"
                className="border-2 border-gray-900"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-2 border-red-500 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('bookings')}
              className={`pb-4 px-2 font-semibold transition-colors ${
                activeTab === 'bookings'
                  ? 'text-yellow-600 border-b-2 border-yellow-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Bookings
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`pb-4 px-2 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'calendar'
                  ? 'text-yellow-600 border-b-2 border-yellow-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Calendar Management
              {blockedDates.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {blockedDates.length}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'bookings' && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-2">Total Bookings</p>
                  <p className="text-4xl font-bold text-gray-900">{bookings.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-2">Paid</p>
                  <p className="text-4xl font-bold text-green-600">
                    {bookings.filter(b => b.payment_status === 'paid').length}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-2">Pending</p>
                  <p className="text-4xl font-bold text-yellow-600">
                    {bookings.filter(b => b.payment_status === 'pending').length}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="mb-6 flex gap-3">
            <Button
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-200 text-gray-700'}
            >
              All ({bookings.length})
            </Button>
            <Button
              onClick={() => setFilter('paid')}
              className={filter === 'paid' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}
            >
              Paid ({bookings.filter(b => b.payment_status === 'paid').length})
            </Button>
            <Button
              onClick={() => setFilter('pending')}
              className={filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700'}
            >
              Pending ({bookings.filter(b => b.payment_status === 'pending').length})
            </Button>
          </div>

          {/* Bookings Table */}
          <Card>
            <CardContent className="pt-6">
              {filteredBookings.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No bookings found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Customer</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Package</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Moving Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4">{booking.customer_name}</td>
                          <td className="py-4 px-4 text-sm">{booking.customer_email}</td>
                          <td className="py-4 px-4">
                            {booking.package_type.replace('_', ' ').replace('plus', '+').toUpperCase()}
                          </td>
                          <td className="py-4 px-4 font-semibold">
                            ${(booking.amount_total / 100).toFixed(2)}
                          </td>
                          <td className="py-4 px-4 text-sm">
                            {booking.moving_date !== 'N/A' ? new Date(booking.moving_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-4 px-4">{getStatusBadge(booking.payment_status)}</td>
                          <td className="py-4 px-4">
                            <Button
                              onClick={() => handleResendEmail(booking.session_id, booking.customer_email)}
                              disabled={resendingId === booking.session_id || booking.customer_email === 'N/A'}
                              className="bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-3"
                            >
                              {resendingId === booking.session_id ? (
                                <>
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Mail className="w-3 h-3 mr-1" />
                                  Resend Email
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Manual Email Form Modal */}
          {showManualEmailForm && (
            <ManualEmailModal
              onClose={() => setShowManualEmailForm(false)}
              onSuccess={() => {
                setShowManualEmailForm(false);
                alert('Email sent successfully!');
              }}
            />
          )}
            </>
          )}

          {/* Calendar Management Tab */}
          {activeTab === 'calendar' && (
            <CalendarManagement
              blockedDates={blockedDates}
              loading={loadingDates}
              onBlockDate={handleBlockDate}
              onUnblockDate={handleUnblockDate}
            />
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Manual Email Modal Component
function ManualEmailModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    package_type: '1_bedroom',
    amount_paid: 165,
    moving_date: '',
    send_to_admin: true
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/send-manual-email`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
    } catch (err) {
      alert('Error sending email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Send Manual Email</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Customer Name *
              </label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Customer Email *
              </label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Package Type *
              </label>
              <select
                value={formData.package_type}
                onChange={(e) => {
                  const amount = e.target.value === '1_bedroom' ? 165 : 325;
                  setFormData({ ...formData, package_type: e.target.value, amount_paid: amount });
                }}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
              >
                <option value="1_bedroom">1 Bedroom ($165)</option>
                <option value="2plus_bedrooms">2+ Bedrooms ($325)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Amount Paid *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount_paid}
                onChange={(e) => setFormData({ ...formData, amount_paid: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Moving Date *
              </label>
              <input
                type="date"
                value={formData.moving_date}
                onChange={(e) => setFormData({ ...formData, moving_date: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="send_to_admin"
                checked={formData.send_to_admin}
                onChange={(e) => setFormData({ ...formData, send_to_admin: e.target.checked })}
                className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
              />
              <label htmlFor="send_to_admin" className="ml-2 text-sm text-gray-700">
                Also send notification to admin email
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={sending}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1 border-2 border-gray-900"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}



// Calendar Management Component
function CalendarManagement({ blockedDates, loading, onBlockDate, onUnblockDate }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [reason, setReason] = useState('');
  const [showBlockForm, setShowBlockForm] = useState(false);

  const blockedDateStrings = blockedDates.map(d => d.date);

  const handleBlockSubmit = (e) => {
    e.preventDefault();
    if (!selectedDate) return;
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    onBlockDate(dateStr, reason);
    setSelectedDate(null);
    setReason('');
    setShowBlockForm(false);
  };

  // Check if a date is blocked (for calendar styling)
  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDateStrings.includes(dateStr);
  };

  // Custom day render to show blocked dates in red
  const modifiers = {
    blocked: (date) => isDateBlocked(date)
  };

  const modifiersStyles = {
    blocked: {
      backgroundColor: '#FEE2E2',
      color: '#DC2626',
      fontWeight: 'bold'
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Calendar className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Calendar Management</h3>
              <p className="text-sm text-blue-700">
                Block specific dates to prevent new bookings. Blocked dates will be disabled in the customer's date picker.
                This does NOT automatically block dates based on MovePro jobs - you control availability manually.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar Picker */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Select Date to Block</h3>
            <div className="flex justify-center">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  if (date && !isDateBlocked(date)) {
                    setShowBlockForm(true);
                  }
                }}
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                className="rounded-md border"
              />
            </div>
            
            {/* Block Date Form */}
            {showBlockForm && selectedDate && (
              <form onSubmit={handleBlockSubmit} className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900 mb-3">
                  Block: {selectedDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <div className="mb-3">
                  <label className="block text-sm text-gray-700 mb-1">
                    Internal Note (optional)
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g., Public holiday, Staff unavailable"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Customers will NOT see this note.</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Block This Date
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowBlockForm(false); setSelectedDate(null); }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Blocked Dates List */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">Blocked Dates ({blockedDates.length})</h3>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 text-gray-400 mx-auto animate-spin" />
              </div>
            ) : blockedDates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No dates blocked</p>
                <p className="text-sm">Select a date on the calendar to block it.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {blockedDates
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((blocked) => {
                    const dateObj = new Date(blocked.date + 'T00:00:00');
                    const isPast = dateObj < new Date(new Date().toDateString());
                    
                    return (
                      <div 
                        key={blocked.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isPast ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div>
                          <p className={`font-semibold ${isPast ? 'text-gray-600' : 'text-gray-900'}`}>
                            {dateObj.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            {isPast && <span className="ml-2 text-xs text-gray-400">(past)</span>}
                          </p>
                          {blocked.reason && (
                            <p className="text-sm text-gray-500">{blocked.reason}</p>
                          )}
                        </div>
                        <Button
                          onClick={() => onUnblockDate(blocked.id)}
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Future Integration Note */}
      <Card className="border border-gray-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold text-gray-700 mb-2">Future Enhancements</h4>
          <ul className="text-sm text-gray-500 space-y-1">
            <li>• Sync with MovePro job calendar (optional)</li>
            <li>• Sync with Google Calendar</li>
            <li>• Set recurring blocked days (e.g., every Sunday)</li>
            <li>• Capacity management (multiple trucks/crews per day)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
